import { lazy } from "react";
import { safeSessionStorage } from "@/lib/safe-storage";

/**
 * Wrapper around React.lazy that handles stale chunk errors from deploys.
 * When a new build is deployed, old chunk filenames become invalid.
 * This catches the import failure and reloads the page once to get fresh references.
 */
function lazyWithRetry(importFn) {
  return lazy(() =>
    importFn().catch(error => {
      // Only reload once per session to avoid infinite loops
      const hasReloaded = safeSessionStorage.getItem("chunk_reload");
      if (!hasReloaded) {
        safeSessionStorage.setItem("chunk_reload", "true");
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      // Already reloaded once, throw the original error
      safeSessionStorage.removeItem("chunk_reload");
      throw error;
    })
  );
}
import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedProjectRoute, ProtectedWorkspaceRoute } from "./guards";
import { SettingsRouteWrapper } from "./SettingsRouteWrapper";
import { PERMISSIONS } from "@/lib/permissions";
import { ACCOUNTS_URL } from "@/lib/constants";
import AppShell from "@/components/layout/AppShell";
import MyDomainsPage from "../../features/domains/pages/MyDomainsPage";
import DomainDetailsPage from "../../features/domains/pages/DomainDetailsPage";
import BuyDomainsPage from "../../features/domains/pages/BuyDomainsPage";
import ThirdPartyRecords from "../../features/domains/pages/ThirdPartyRecords";
import DomainConnectionPage from "../../features/domains/pages/DomainConnectionPage";

// Lazy load pages for code splitting (with retry for stale deploy chunks)
const WorkspacePage = lazyWithRetry(
  () => import("@/features/workspace/pages/WorkspacePage")
);
const HomePage = lazyWithRetry(() => import("@/features/home/pages/HomePage"));
const OverviewPage = lazyWithRetry(
  () => import("@/features/overview/pages/OverviewPage")
);
const ProfileSettingsPage = lazyWithRetry(
  () => import('@/features/profile/pages/ProfileSettingsPage')
)
const FeedbackPage = lazyWithRetry(
  () => import('@/features/feedback/pages/FeedbackPage')
)

const AnalyticsPage = lazyWithRetry(
  () => import("@/features/analytics/pages/AnalyticsPage")
);
const SitePagesPage = lazyWithRetry(
  () => import("@/features/site-pages/pages/SitePagesPage")
);
const SharedProjectsPage = lazyWithRetry(
  () => import("@/features/workspace/pages/SharedProjectsPage")
);
const DraftGenerationsPage = lazyWithRetry(
  () => import("@/features/draft-generations/pages/DraftGenerationsPage")
);
const CmsPage = lazyWithRetry(() => import("@/features/cms/pages/CmsPage"));

const PageSettingsPage = lazyWithRetry(
  () => import("@/features/site-pages/pages/PageSettingsPage")
);
const ComponentsPage = lazyWithRetry(
  () => import("@/features/site-pages/pages/ComponentsPage")
);
const BlocksPage = lazyWithRetry(
  () => import("@/features/site-pages/pages/BlocksPage")
);
const BlogLayout = lazyWithRetry(
  () => import("@/features/blog/pages/BlogLayout")
);
const BlogPostsPage = lazyWithRetry(
  () => import("@/features/blog/pages/BlogPostsPage")
);
const BlogArchivedPage = lazyWithRetry(
  () => import("@/features/blog/pages/BlogArchivedPage")
);
const BlogEditorPage = lazyWithRetry(
  () => import("@/features/blog/pages/BlogEditorPage")
);
const BlogTagsPage = lazyWithRetry(
  () => import("@/features/blog/pages/BlogTagsPage")
);
const BlogTagEditorPage = lazyWithRetry(
  () => import("@/features/blog/pages/BlogTagEditorPage")
);
const FormsPage = lazyWithRetry(
  () => import("@/features/forms/pages/FormsPage")
);
const FormDetailsPage = lazyWithRetry(
  () => import("@/features/forms/pages/FormDetailsPage")
);
const OrdersPage = lazyWithRetry(
  () => import("@/features/orders/pages/OrdersPage")
);
const ProductsPage = lazyWithRetry(
  () => import("@/features/products/pages/ProductsPage")
);
const ContactsPage = lazyWithRetry(
  () => import("@/features/contacts/pages/ContactsPage")
);
const NotificationsPage = lazyWithRetry(
  () => import("@/features/email-notifications/pages/NotificationsPage")
);
const WebhooksPage = lazyWithRetry(
  () => import("@/features/webhooks/pages/WebhooksPage")
);
const IntegrationsPage = lazyWithRetry(
  () => import("@/features/integrations/pages/IntegrationsPage")
);
const ChatbotLayout = lazyWithRetry(
  () => import("@/features/chatbot/pages/ChatbotLayout")
);
const ChatbotManageLayout = lazyWithRetry(
  () => import("@/features/chatbot/pages/ChatbotManageLayout")
);
const ChatbotInboxPage = lazyWithRetry(
  () => import("@/features/chatbot/pages/InboxPage")
);
const ChatbotTrainingPage = lazyWithRetry(
  () => import("@/features/chatbot/pages/TrainingPage")
);
const ChatbotDesignPage = lazyWithRetry(
  () => import("@/features/chatbot/pages/DesignPage")
);
const ChatbotAutomationsPage = lazyWithRetry(
  () => import("@/features/chatbot/pages/AutomationsPage")
);
const ChatbotTestPage = lazyWithRetry(
  () => import("@/features/chatbot/pages/TestPage")
);
const ChatbotInstallPage = lazyWithRetry(
  () => import("@/features/chatbot/pages/InstallPage")
);
const ChatbotAnalyticsPage = lazyWithRetry(
  () => import("@/features/chatbot/pages/AnalyticsPage")
);
const ChatbotSettingsPage = lazyWithRetry(
  () => import("@/features/chatbot/pages/SettingsPage")
);
const MediaLibraryPage = lazyWithRetry(
  () => import("@/features/media/pages/MediaLibraryPage")
);
const FileManagerPage = lazyWithRetry(
  () => import("@/features/media/pages/FileManagerPage")
);
// Remove unused DomainsPage import - using BuyDomainsPage instead
const DomainConfigPage = lazyWithRetry(
  () => import("@/features/domains/pages/DomainConfigPage")
);

const SEOPage = lazyWithRetry(
  () => import("@/features/settings/pages/SEOPage")
);
const SiteSpeedPage = lazyWithRetry(
  () => import("@/features/settings/pages/SiteSpeedPage")
);
const GeneralSettingsPage = lazyWithRetry(
  () => import("@/features/settings/pages/GeneralSettingsPage")
);
const GuestAccessPage = lazyWithRetry(
  () => import("@/features/settings/pages/GuestAccessPage")
);
const WordpressPage = lazyWithRetry(
  () => import("@/features/settings/pages/WordpressPage")
);
const DomainPage = lazyWithRetry(
  () => import("@/features/settings/pages/DomainPage")
);

const TeamPage = lazyWithRetry(() => import("@/features/team/pages/TeamPage"));
const WorkspaceManagementPage = lazyWithRetry(
  () => import("@/features/workspace/pages/WorkspaceManagementPage")
);
const WorkspaceSettingsPage = lazyWithRetry(
  () => import("@/features/workspace/pages/WorkspaceSettingsPage")
);
const AddOnPage = lazyWithRetry(
  () => import("@/features/add-ons/pages/AddOnPage")
);
const CalendarPage = lazyWithRetry(
  () => import("@/features/social-media/pages/CalendarPage")
);
const ChannelsPage = lazyWithRetry(
  () => import("@/features/social-media/pages/ChannelsPage")
);
const CreatePage = lazyWithRetry(
  () => import("@/features/social-media/pages/CreatePage")
);
const UsagePage = lazyWithRetry(
  () => import("@/features/usage/pages/UsagePage")
);
const BillingPage = lazyWithRetry(
  () => import("@/features/billing/pages/BillingPage")
);
const SeoAuditPage = lazyWithRetry(
  () => import("@/features/seo-audit/pages/SeoAuditPage")
);
const IssueSettingsPage = lazyWithRetry(
  () => import("@/features/seo-audit/pages/IssueSettingsPage")
);
const IssueDetailPage = lazyWithRetry(
  () => import("@/features/seo-audit/pages/IssueDetailPage")
);
const AddSitePage = lazyWithRetry(
  () => import("@/features/seo-audit/pages/AddSitePage")
);
const ProjectOverviewPage = lazyWithRetry(
  () => import("@/features/seo-audit/pages/ProjectOverviewPage")
);
const CrawlingProgressPage = lazyWithRetry(
  () => import("@/features/seo-audit/pages/CrawlingProgressPage")
);
const PricingPage = lazyWithRetry(
  () => import("@/features/pricing/pages/PricingPage")
);
const ComingSoonAppPage = lazyWithRetry(
  () => import("@/features/coming-soon/pages/ComingSoonAppPage")
);
const CalendarLayout = lazyWithRetry(
  () => import("@/features/calendar/pages/CalendarLayout")
);
const CalendarEventTypesPage = lazyWithRetry(
  () => import("@/features/calendar/pages/EventTypesPage")
);
const CalendarEventTypeEditorPage = lazyWithRetry(
  () => import("@/features/calendar/pages/EventTypeEditorPage")
);
const CalendarBookingsPage = lazyWithRetry(
  () => import("@/features/calendar/pages/BookingsPage")
);
const CalendarAvailabilityPage = lazyWithRetry(
  () => import("@/features/calendar/pages/AvailabilityPage")
);
const CalendarIntegrationsPage = lazyWithRetry(
  () => import("@/features/calendar/pages/IntegrationsPage")
);
const CalendarAnalyticsPage = lazyWithRetry(
  () => import("@/features/calendar/pages/AnalyticsPage")
);
const CookieManagerLayout = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/CookieManagerLayout")
);
const CookieBannersPage = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/BannersPage")
);
const CookieBannerDesignPage = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/BannerDesignPage")
);
const CookieMappingsPage = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/CookiesPage")
);
const CookieScansPage = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/ScansPage")
);
const CookieInstallPage = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/InstallPage")
);
const CookieAnalyticsPage = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/AnalyticsPage")
);
const CookieConsentLogsPage = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/ConsentLogsPage")
);
const CookieSettingsPage = lazyWithRetry(
  () => import("@/features/cookie-manager/pages/SettingsPage")
);
const EmailAutomationPage = lazyWithRetry(
  () => import("@/features/email-automation/pages/EmailAutomationPage")
);
const EmailAutomationAllContactsPage = lazyWithRetry(
  () => import("@/features/email-automation/pages/AllContactsPage")
);
const EmailAutomationTagDetailComponent = lazyWithRetry(
  () => import("@/features/email-automation/components/TagDetailComponent")
);
const EmailAutomationSegmentDetailComponent = lazyWithRetry(
  () => import("@/features/email-automation/components/SegmentDetailComponent")
);
const EmailAutomationMailerDetailComponent = lazyWithRetry(
  () => import("@/features/email-automation/components/MailerDetailComponent")
);
const EmailAutomationCampaignDetailComponent = lazyWithRetry(
  () => import("@/features/email-automation/components/CampaignDetailComponent")
);
const EmailAutomationContactDetailComponent = lazyWithRetry(
  () => import("@/features/email-automation/components/ContactDetailComponent")
);
const EmailAutomationLogDetailComponent = lazyWithRetry(
  () => import("@/features/email-automation/components/EmailLogDetailComponent")
);
const EmailAutomationAutomationDetailPage = lazyWithRetry(
  () => import("@/features/email-automation/pages/AutomationsPage")
);
const FormBuilderLayout = lazyWithRetry(
  () => import("@/features/form-builder/pages/FormBuilderLayout")
);
const FormsDashboardPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/FormsDashboardPage")
);
const FormBuilderEditorPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/FormEditorPage")
);
const FormBuilderGeneralPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/GeneralPage")
);
const FormBuilderWorkflowPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/WorkflowPage")
);
const FormBuilderAppearancePage = lazyWithRetry(
  () => import("@/features/form-builder/pages/DesignPage")
);
const FormBuilderInstallPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/InstallPage")
);
const FormBuilderAnalyticsPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/AnalyticsPage")
);
const FormBuilderResponsesPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/ResponsesPage")
);
const FormBuilderResponseDetailPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/ResponseDetailPage")
);
const FormBuilderSettingsPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/SettingsPage")
);
const FormBuilderContactsPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/ContactsPage")
);
const FormBuilderFormContactsPage = lazyWithRetry(
  () => import("@/features/form-builder/pages/FormContactsPage")
);

const TemplatesPage = lazyWithRetry(
  () => import("@/features/templates/pages/TemplatesPage")
);
const TemplateDetailPage = lazyWithRetry(
  () => import("@/features/templates/pages/TemplateDetailPage")
);

// AccountsRedirect - redirects auth pages to external accounts service
// When VITE_USE_LOCAL_AUTH is set, show local login page instead
const useLocalAuth = import.meta.env.VITE_USE_LOCAL_AUTH === "true";
const LoginPage = useLocalAuth
  ? lazyWithRetry(() => import("@/features/auth/pages/LoginPage"))
  : null;

function AccountsRedirect({ page }) {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `${ACCOUNTS_URL}/${page}?redirect=${encodeURIComponent(redirectUrl)}`;
  return null;
}
const AIOnboardPage = lazyWithRetry(() => import('@/features/ai-onboard/index'))
const ImageLibraryPage = lazyWithRetry(() => import('@/features/image-library/pages/ImageLibraryPage'))
const SitemapCanvasPage = lazyWithRetry(
  () => import("@/features/sitemap/pages/SitemapCanvasPage")
);
const InvitationPage = lazyWithRetry(
  () => import("@/features/team/pages/InvitationPage")
);
const LoaderV2Demo = lazyWithRetry(
  () => import("@/components/ui/LoaderV2Demo")
);

// Error pages
const UnauthorizedPage = lazyWithRetry(
  () => import("@/components/error/UnauthorizedPage")
);

export default function AppRoutes() {
  return (
    <Routes>
      {/* Main app routes wrapped in AppShell */}
      <Route path="/" element={<AppShell />}>
        {/* Home and workspace project routes */}
        <Route path="ai" element={<AIOnboardPage />} />
        <Route path="image-library" element={<ProtectedWorkspaceRoute><ImageLibraryPage /></ProtectedWorkspaceRoute>} />

        <Route
          index
          element={
            <ProtectedWorkspaceRoute permission={PERMISSIONS.VIEW_PROJECT}>
              <HomePage />
            </ProtectedWorkspaceRoute>
          }
        />

        <Route
          path="projects"
          element={
            <ProtectedWorkspaceRoute permission={PERMISSIONS.VIEW_PROJECT}>
              <WorkspacePage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Overview & Analytics */}
        <Route
          path="overview"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.VIEW_PROJECT}>
              <OverviewPage />
            </ProtectedProjectRoute>
          }
        />
        {/* Sitemap canvas (sitemap-first projects) */}
        <Route
          path="sitemap"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.VIEW_PROJECT}>
              <SitemapCanvasPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <ProtectedProjectRoute>
              <AnalyticsPage />
            </ProtectedProjectRoute>
          }
        />

        {/* Site Pages */}
        <Route
          path="pages"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.PAGES_READ}>
              <SitePagesPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="shared-projects"
          element={
            <ProtectedWorkspaceRoute>
              <SharedProjectsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="draft-generations"
          element={
            <ProtectedWorkspaceRoute permission={PERMISSIONS.VIEW_PROJECT}>
              <DraftGenerationsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="feedback/:requestId" element={<FeedbackPage />} />
        <Route
          path="cms"
          element={
            <ProtectedWorkspaceRoute permission={PERMISSIONS.VIEW_PROJECT}>
              <CmsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="pages/:pageSlug/settings"
          element={
            <ProtectedProjectRoute
              any={[PERMISSIONS.PAGES_READ, PERMISSIONS.PAGES_WRITE]}
            >
              <PageSettingsPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="components"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.COMPONENTS_READ}>
              <ComponentsPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="blocks"
          element={
            <ProtectedProjectRoute>
              <BlocksPage />
            </ProtectedProjectRoute>
          }
        />

        {/* Content — Blog CMS */}
        <Route
          path="blog"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.BLOG_READ}>
              <BlogLayout />
            </ProtectedProjectRoute>
          }
        >
          <Route index element={<BlogPostsPage />} />
          <Route path="drafts" element={<BlogPostsPage view="draft" />} />
          <Route
            path="published"
            element={<BlogPostsPage view="published" />}
          />
          <Route path="archived" element={<BlogArchivedPage />} />
          <Route path="tags" element={<BlogTagsPage />} />
          <Route
            path="tags/new"
            element={
              <ProtectedProjectRoute permission={PERMISSIONS.BLOG_WRITE}>
                <BlogTagEditorPage />
              </ProtectedProjectRoute>
            }
          />
          <Route path="tags/:id" element={<BlogTagEditorPage />} />
          <Route
            path="editor/new"
            element={
              <ProtectedProjectRoute permission={PERMISSIONS.BLOG_WRITE}>
                <BlogEditorPage />
              </ProtectedProjectRoute>
            }
          />
          <Route path="editor/:id" element={<BlogEditorPage />} />
        </Route>

        {/* Customer & Leads */}
        <Route
          path="forms"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.FORMS_READ}>
              <FormsPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="forms/:formId"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.FORMS_READ}>
              <FormDetailsPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="contacts"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.CONTACTS_READ}>
              <ContactsPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="orders"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.ORDERS_READ}>
              <OrdersPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="products"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.PRODUCTS_READ}>
              <ProductsPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.SITE_SETTINGS_READ}>
              <NotificationsPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="webhooks"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.SITE_SETTINGS_READ}>
              <WebhooksPage />
            </ProtectedProjectRoute>
          }
        />

        {/* Integrations */}
        <Route
          path="integrations"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.INTEGRATIONS_READ}>
              <IntegrationsPage />
            </ProtectedProjectRoute>
          }
        />

        {/* Chatbot (workspace-scoped) */}
        <Route
          path="chatbot"
          element={
            <ProtectedWorkspaceRoute>
              <ChatbotLayout />
            </ProtectedWorkspaceRoute>
          }
        >
          <Route index element={<ChatbotInboxPage />} />
          <Route path="inbox" element={<ChatbotInboxPage />} />
          <Route path="manage" element={<ChatbotManageLayout />}>
            <Route index element={<ChatbotTrainingPage />} />
            <Route path="design" element={<ChatbotDesignPage />} />
            <Route path="automations" element={<ChatbotAutomationsPage />} />
            <Route path="test" element={<ChatbotTestPage />} />
            <Route path="install" element={<ChatbotInstallPage />} />
            <Route path="analytics" element={<ChatbotAnalyticsPage />} />
            <Route path="settings" element={<ChatbotSettingsPage />} />
          </Route>
        </Route>

        {/* Media & Files */}
        <Route
          path="media"
          element={
            <ProtectedProjectRoute>
              <MediaLibraryPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="files"
          element={
            <ProtectedProjectRoute>
              <FileManagerPage />
            </ProtectedProjectRoute>
          }
        />

        {/* Domains - Workspace Level Routes */}
        <Route
          path="domains"
          element={<Navigate to="/domains/my-domains" replace />}
        />
        <Route path="domains/buy-domains" element={<BuyDomainsPage />} />
        {/* <Route
          path="domainConfig"
          element={
            <ProtectedWorkspaceRoute>
              <DomainConfigPage />
            </ProtectedWorkspaceRoute>
          }
        /> */}
        <Route
          path="domains/my-domains"
          element={
            <ProtectedWorkspaceRoute>
              <MyDomainsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="domains/my-domains/:domainName"
          element={
            <ProtectedWorkspaceRoute>
              <DomainDetailsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="domains/my-domain/records"
          element={
            <ProtectedWorkspaceRoute>
              <ThirdPartyRecords />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="domains/my-domain/validate"
          element={
            <ProtectedWorkspaceRoute>
              <DomainConnectionPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="domains/my-domains/config/:domainName"
          element={
            <ProtectedWorkspaceRoute>
              <DomainConfigPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedWorkspaceRoute>
              <ProfileSettingsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        {/* General Settings */}
        <Route
          path="settings"
          element={
            <SettingsRouteWrapper projectComponent={GeneralSettingsPage} />
          }
        />
        <Route
          path="guest-access"
          element={
            <ProtectedProjectRoute
              any={[
                PERMISSIONS.SITE_SETTINGS_WRITE,
                PERMISSIONS.SITE_SETTINGS_READ,
              ]}
            >
              <GuestAccessPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="seo"
          element={
            <ProtectedProjectRoute
              permission={PERMISSIONS.PROJECT_SETTINGS_READ}
            >
              <SEOPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="site-speed"
          element={
            <ProtectedProjectRoute
              permission={PERMISSIONS.PROJECT_SETTINGS_READ}
            >
              <SiteSpeedPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="wordpress"
          element={
            <ProtectedProjectRoute
              any={[PERMISSIONS.WORDPRESS_READ, PERMISSIONS.WORDPRESS_WRITE]}
            >
              <WordpressPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="domain"
          element={
            <ProtectedProjectRoute permission={PERMISSIONS.DOMAIN_READ}>
              <DomainPage />
            </ProtectedProjectRoute>
          }
        />
        <Route
          path="domain/connection"
          element={
            <ProtectedProjectRoute>
              <DomainConnectionPage />
            </ProtectedProjectRoute>
          }
        />

        {/* Team - can be both workspace and project level */}
        <Route
          path="team"
          element={
            <ProtectedWorkspaceRoute
              any={[PERMISSIONS.TEAM_SETTINGS, PERMISSIONS.TEAM_SETTINGS_READ]}
            >
              <TeamPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="teams"
          element={
            <ProtectedWorkspaceRoute
              any={[PERMISSIONS.TEAM_SETTINGS, PERMISSIONS.TEAM_SETTINGS_READ]}
            >
              <TeamPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Social Media */}
        <Route
          path="social-media/calendar"
          element={
            <ProtectedWorkspaceRoute>
              <CalendarPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="social-media/channels"
          element={
            <ProtectedWorkspaceRoute>
              <ChannelsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="social-media/create"
          element={
            <ProtectedWorkspaceRoute>
              <CreatePage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* New secondary apps waitlists */}
        <Route
          path="project-management"
          element={
            <ProtectedWorkspaceRoute>
              <ComingSoonAppPage
                key="project-management"
                appKey="projectManagement"
              />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="image-generation"
          element={
            <ProtectedWorkspaceRoute>
              <ComingSoonAppPage
                key="image-generation"
                appKey="imageGeneration"
              />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="form-builder"
          element={
            <ProtectedWorkspaceRoute>
              <FormBuilderLayout />
            </ProtectedWorkspaceRoute>
          }
        >
          <Route index element={<FormsDashboardPage />} />
          <Route path="contacts" element={<FormBuilderContactsPage />} />
          <Route
            path="contact-lists"
            element={<Navigate to="../contacts" relative="path" replace />}
          />
          <Route path=":formId" element={<FormBuilderEditorPage />} />
          <Route path=":formId/general" element={<FormBuilderGeneralPage />} />
          <Route
            path=":formId/workflow"
            element={<FormBuilderWorkflowPage />}
          />
          <Route
            path=":formId/appearance"
            element={<FormBuilderAppearancePage />}
          />
          <Route
            path=":formId/design"
            element={<Navigate to="../appearance" relative="path" replace />}
          />
          <Route path=":formId/install" element={<FormBuilderInstallPage />} />
          <Route
            path=":formId/share"
            element={<Navigate to="../install" relative="path" replace />}
          />
          <Route
            path=":formId/analytics"
            element={<FormBuilderAnalyticsPage />}
          />
          <Route
            path=":formId/responses"
            element={<FormBuilderResponsesPage />}
          />
          <Route
            path=":formId/responses/:responseId"
            element={<FormBuilderResponseDetailPage />}
          />
          <Route
            path=":formId/contacts"
            element={<FormBuilderFormContactsPage />}
          />
          <Route
            path=":formId/settings"
            element={<FormBuilderSettingsPage />}
          />
        </Route>
        {/* Website Audit */}
        <Route
          path="seo-audit"
          element={
            <ProtectedWorkspaceRoute>
              <SeoAuditPage />
            </ProtectedWorkspaceRoute>
          }
        />
        {/* Issue Settings */}
        <Route
          path="seo-audit/issue-settings"
          element={
            <ProtectedWorkspaceRoute>
              <IssueSettingsPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Add New Site - standalone page (before seo-audit/:id) */}
        <Route
          path="seo-audit/add"
          element={
            <ProtectedWorkspaceRoute>
              <AddSitePage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Project Overview - MUST come BEFORE seo-audit/:id */}
        <Route
          path="seo-audit/:id/overview"
          element={
            <ProtectedWorkspaceRoute>
              <ProjectOverviewPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Crawling Progress Page */}
        <Route
          path="seo-audit/:id/crawling"
          element={
            <ProtectedWorkspaceRoute>
              <CrawlingProgressPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Issue Detail Page - for specific issue details */}
        <Route
          path="seo-audit/:id/issue/:issueSlug"
          element={
            <ProtectedWorkspaceRoute>
              <IssueDetailPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Project overview */}
        <Route
          path="seo-audit/:id"
          element={
            <ProtectedWorkspaceRoute>
              <ProjectOverviewPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Usage */}
        <Route
          path="usage"
          element={
            <ProtectedWorkspaceRoute permission={PERMISSIONS.USAGE_READ}>
              <UsagePage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Templates */}
        <Route
          path="templates"
          element={
            <ProtectedWorkspaceRoute>
              <TemplatesPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="templates/:id"
          element={
            <ProtectedWorkspaceRoute>
              <TemplateDetailPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Calendar */}
        <Route
          path="calendar"
          element={
            <ProtectedWorkspaceRoute>
              <CalendarLayout />
            </ProtectedWorkspaceRoute>
          }
        >
          <Route index element={<CalendarEventTypesPage />} />
          <Route
            path="event-types/:id"
            element={<CalendarEventTypeEditorPage />}
          />
          <Route path="bookings" element={<CalendarBookingsPage />} />
          <Route path="availability" element={<CalendarAvailabilityPage />} />
          <Route path="integrations" element={<CalendarIntegrationsPage />} />
          <Route path="analytics" element={<CalendarAnalyticsPage />} />
        </Route>

        {/* Cookie Manager */}
        {/* Cookie Manager — TEMPORARILY DISABLED (feature not launched yet).
            The parent route redirects to the dashboard, so none of the cookie
            pages mount and NO cookie API calls fire (even via a direct URL).
            Restore by reinstating <CookieManagerLayout/> as the element. */}
        <Route
          path="cookie-manager"
          element={
            <ProtectedWorkspaceRoute>
              <CookieManagerLayout />
            </ProtectedWorkspaceRoute>
          }
        >
          <Route index element={<CookieBannersPage />} />
          <Route path=":bannerId" element={<Navigate to="design" replace />} />
          <Route path=":bannerId/design" element={<CookieBannerDesignPage />} />
          <Route path=":bannerId/cookies" element={<CookieMappingsPage />} />
          <Route path=":bannerId/scans" element={<CookieScansPage />} />
          <Route path=":bannerId/install" element={<CookieInstallPage />} />
          <Route path=":bannerId/analytics" element={<CookieAnalyticsPage />} />
          <Route
            path=":bannerId/consent-logs"
            element={<CookieConsentLogsPage />}
          />
          <Route path=":bannerId/settings" element={<CookieSettingsPage />} />
        </Route>

        {/* Email Automations */}
        <Route
          path="email-automation"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/contacts"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationAllContactsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/:section"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/tags/:tagId"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationTagDetailComponent />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/segments/:segmentId"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationSegmentDetailComponent />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/mailers/:mailerId"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationMailerDetailComponent />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/campaigns/:campaignId"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationCampaignDetailComponent />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/contacts/:contactId"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationContactDetailComponent />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/email-history/:logId"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationLogDetailComponent />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="email-automation/automations/:automationId"
          element={
            <ProtectedWorkspaceRoute>
              <EmailAutomationAutomationDetailPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Billing */}
        <Route
          path="billing"
          element={
            <ProtectedWorkspaceRoute>
              <BillingPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Pricing */}
        <Route
          path="pricing"
          element={
            <ProtectedWorkspaceRoute>
              <PricingPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Workspace Management */}
        <Route
          path="settings/workspace-management"
          element={
            <ProtectedWorkspaceRoute
              any={[
                PERMISSIONS.WORKSPACE_SETTINGS,
                PERMISSIONS.WORKSPACE_SETTINGS_READ,
              ]}
            >
              <WorkspaceManagementPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="settings/workspace-settings"
          element={
            <ProtectedWorkspaceRoute
              any={[
                PERMISSIONS.WORKSPACE_SETTINGS,
                PERMISSIONS.WORKSPACE_SETTINGS_READ,
              ]}
            >
              <WorkspaceSettingsPage />
            </ProtectedWorkspaceRoute>
          }
        />
        <Route
          path="add-ons/:slug"
          element={
            <ProtectedWorkspaceRoute>
              <AddOnPage />
            </ProtectedWorkspaceRoute>
          }
        />

        {/* Error pages */}
        <Route path="unauthorized" element={<UnauthorizedPage />} />
        {/* <Route path="*" element={<NotFoundPage />} /> */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>

      {/* Auth routes - local login page or redirect to external accounts service */}
      {useLocalAuth ? (
        <>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<LoginPage />} />
          <Route path="/forgot-password" element={<LoginPage />} />
        </>
      ) : (
        <>
          <Route path="/login" element={<AccountsRedirect page="login" />} />
          <Route
            path="/register"
            element={<AccountsRedirect page="register" />}
          />
          <Route
            path="/forgot-password"
            element={<AccountsRedirect page="forgot-password" />}
          />
        </>
      )}

      {/* Invitation acceptance page (outside AppShell) */}
      <Route path="/invite/:invitationId" element={<InvitationPage />} />
      <Route path="/loader-demo" element={<LoaderV2Demo />} />

      {/* Catch all for routes outside /v2 */}
      {/* <Route path="*" element={<NotFoundPage />} /> */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
